import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, UserCog, Bot, MessagesSquare, Users, CheckCheck, Info, XCircle, GraduationCap, ShoppingBag, UserCheck, ListChecks, Megaphone } from 'lucide-react';
import { anuncioDelContacto } from '@/lib/anuncio';
import { Link } from 'react-router-dom';
import { PasarVenta } from '@/components/tienda/PasarVenta';
import { PasarChat } from './PasarChat';
import * as supervisionApi from '@/api/supervision.api';
import * as seguimientoApi from '@/api/seguimiento.api';
import * as conversationsApi from '@/api/conversations.api';
import * as trainingApi from '@/api/training.api';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { MessageBubble } from './MessageBubble';
import { AgentStatusDot } from './AgentStatusDot';
import { ContactDetailsPanel } from '@/components/contacts/ContactDetailsPanel';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';
import { useMenu, useVeRuta } from '@/hooks/useMenu';
import { statusBadge } from '@/lib/badges';
import { getContactDisplayName, getContactSubtitle } from '@/lib/contact-display';

export function ConversationChat({ conversationId }: { conversationId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuthStore((s) => s.user);
  const [draft, setDraft] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [pasandoVenta, setPasandoVenta] = useState(false);
  const [marcandoSeguimiento, setMarcandoSeguimiento] = useState(false);
  const [notaSeguimiento, setNotaSeguimiento] = useState('');
  const [volverEl, setVolverEl] = useState('');
  // El botón existe donde hay tienda: el módulo de ventas aporta rutas al negocio aunque
  // la asesora no las tenga en su menú, así que se mira el inventario del menú completo.
  const hayTienda = useMenu().datos?.tienda === true;
  const scrollRef = useRef<HTMLDivElement>(null);

  const convo = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationsApi.getConversation(conversationId as string),
    enabled: Boolean(conversationId),
  });

  const messages = useQuery({
    queryKey: ['conversation', conversationId, 'messages'],
    queryFn: () => conversationsApi.listMessages(conversationId as string, { take: 200 }),
    enabled: Boolean(conversationId),
  });

  // ¿Quedó en comprar? El caso abierto de este chat, si lo hay (solo en tiendas).
  const seguimiento = useQuery({
    queryKey: ['seguimiento', 'chat', conversationId],
    queryFn: () => seguimientoApi.delChat(conversationId as string),
    enabled: Boolean(conversationId) && hayTienda,
    retry: false,
  });
  const abrirSeguimiento = useMutation({
    mutationFn: () =>
      seguimientoApi.quedoEnComprar(conversationId as string, {
        nota: notaSeguimiento.trim() || null,
        volverEl: volverEl ? new Date(`${volverEl}T09:00:00`).toISOString() : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seguimiento'] });
      setMarcandoSeguimiento(false);
      setNotaSeguimiento('');
      setVolverEl('');
      toast.success('Anotado: sale en Seguimiento hasta que compre o diga que no.');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo anotar'),
  });

  // Por qué NO se puede escribir en este chat, o `null` si se puede.
  const extra = convo.data as
    | {
        esRelevo?: boolean;
        channel?: { simulacro?: boolean };
        assignedUser?: { id: string; name: string | null; email: string } | null;
        tags?: Array<{ tag: { id: string; name: string } }>;
      }
    | undefined;
  const porQueNoSeEscribe = extra?.esRelevo
    ? 'Las respuestas de este chat salen del sistema de RESIST, no de aquí. Aquí solo se lee.'
    : extra?.channel?.simulacro
      ? 'Este número está en simulacro: lo que escribas no le llegaría a nadie. Aquí solo se lee hasta que le quites el simulacro al canal.'
      : null;

  const ordered = [...(messages.data?.items ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [ordered.length, conversationId, messages.dataUpdatedAt]);

  const send = useMutation({
    mutationFn: (content: string) => conversationsApi.sendMessage(conversationId as string, content),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => toast.error('No se pudo enviar el mensaje'),
  });

  const takeControl = useMutation({
    mutationFn: async (toHuman: boolean) => {
      const id = conversationId as string;
      // A la asesora el chat ya le tocó en el reparto: tomar o devolver el control solo
      // cambia quién contesta (ella o el asistente). Moverlo de persona es de quien supervisa.
      const esAsesora = me?.role === 'AGENT';
      if (toHuman) {
        if (!esAsesora) await conversationsApi.assignUser(id, me?.id ?? null);
        return conversationsApi.updateStatus(id, 'WAITING_HUMAN');
      }
      if (!esAsesora) await conversationsApi.assignUser(id, null);
      return conversationsApi.updateStatus(id, 'ACTIVE');
    },
    onSuccess: (_res, toHuman) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      // Confirmación visible: el cambio de badge solo es demasiado sutil.
      toast.success(toHuman ? 'Tomaste el control de la conversación' : 'Conversación devuelta al asistente');
    },
    onError: () => toast.error('No se pudo cambiar el control'),
  });

  // Cerrar conversación (alta A2 de la auditoría): la salida del embudo de
  // "Atención" — sin esto la bandeja nunca queda en cero. El agente NO
  // responde en CLOSED; un mensaje nuevo del cliente la reabre (flujo existente).
  const closeConversation = useMutation({
    mutationFn: () => conversationsApi.updateStatus(conversationId as string, 'CLOSED'),
    onSuccess: () => {
      setConfirmClose(false);
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversación cerrada');
    },
    onError: () => toast.error('No se pudo cerrar la conversación'),
  });

  // Modo entrenamiento: el agente calla y cada respuesta tuya se captura como
  // ejemplo. Al salir, el agente vuelve a responder (la conversación pasa a ACTIVE).
  // Entrenar al agente es de quien administra el sistema. A los demás el botón
  // solo les sale si el chat YA está entrenando, para poder terminarlo.
  const entrena = useVeRuta('/entrenamiento');
  // Mover un chat de asesora es de quien supervisa: la misma llave que la pantalla de Supervisión.
  const supervisa = useVeRuta('/supervision');
  const asignada = extra?.assignedUser
    ? { id: extra.assignedUser.id, nombre: extra.assignedUser.name?.trim() || extra.assignedUser.email }
    : null;
  // «Vino referido»: lo dice quien atiende, y Supervisión lo cuenta aparte de los anuncios.
  const esReferido = (extra?.tags ?? []).some((t) => t.tag.name === 'Referido');
  const referido = useMutation({
    mutationFn: (si: boolean) => supervisionApi.marcarReferido(conversationId as string, si),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['supervision'] });
      toast.success(r.referido ? 'Anotado: este cliente vino referido.' : 'Se quitó la marca de referido.');
    },
    onError: () => toast.error('No se pudo marcar como referido'),
  });
  const training = useMutation({
    mutationFn: (on: boolean) =>
      on
        ? trainingApi.startConversationTraining(conversationId as string)
        : trainingApi.stopConversationTraining(conversationId as string),
    onSuccess: (_res, on) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success(
        on
          ? 'Modo entrenamiento: el agente no responde; tus respuestas se capturan como ejemplos'
          : 'Entrenamiento terminado: el agente vuelve a responder',
      );
    },
    onError: () => toast.error('No se pudo cambiar el modo entrenamiento'),
  });

  const markRead = useMutation({
    mutationFn: () => conversationsApi.markRead(conversationId as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => toast.error('No se pudo marcar como leída'),
  });

  // Auto-marcar como leída al abrir la conversación y cada vez que llega un
  // mensaje nuevo mientras está abierta (v0.5). Debounce de 1s: si el unreadCount
  // sube de nuevo, se reprograma; tras marcar, el refetch lo deja en 0 y para.
  const unreadCount = convo.data?.unreadCount ?? 0;
  const markReadRef = useRef(() => {});
  markReadRef.current = () => {
    if (!markRead.isPending) markRead.mutate();
  };
  useEffect(() => {
    if (!conversationId || unreadCount <= 0) return;
    const t = setTimeout(() => markReadRef.current(), 1000);
    return () => clearTimeout(t);
  }, [conversationId, unreadCount]);

  if (!conversationId) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-slate-950">
        <EmptyState
          icon={MessagesSquare}
          title="Selecciona una conversación"
          description="Elige una conversación de la lista para ver los mensajes."
        />
      </div>
    );
  }

  if (convo.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-slate-950">
        <Spinner size={28} />
      </div>
    );
  }

  const c = convo.data;
  const isGroup = c?.contact?.isGroup ?? false;
  const anuncio = isGroup ? null : anuncioDelContacto(c?.contact);
  const name = getContactDisplayName(c?.contact);
  const badge = c ? statusBadge(c.status) : null;
  const isHumanControlled = c?.status === 'WAITING_HUMAN';
  const channelSuffix = c?.channel?.name ? ` · ${c.channel.name}` : '';
  const subtitle = isGroup
    ? `${c?.contact?.memberCount ? `${c.contact.memberCount} miembros` : 'Grupo'}${channelSuffix}`
    : `${getContactSubtitle(c?.contact)}${channelSuffix}`;

  return (
    <div className="flex min-w-0 flex-1 flex-col bg-surface">
      {/* Header. `flex-wrap`: en un teléfono los botones bajan a una segunda fila en vez de
          salirse de la pantalla (así no se veía «Pasar venta»). */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-surface-border bg-surface px-3 py-2 lateral:px-4">
        <Avatar
          name={name}
          src={c?.contact?.avatarUrl}
          isGroup={isGroup}
          seed={c?.contact?.externalId}
          size="md"
        />
        <div className="min-w-[9rem] flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {isGroup && <Users className="h-4 w-4 shrink-0 text-slate-400" />}
            <span className="truncate">{name}</span>
          </p>
          <p className="truncate text-xs text-slate-400">{subtitle}</p>
        </div>
        {c && !isGroup && <AgentStatusDot conversation={c} />}
        {isGroup && <Badge variant="info">Grupo</Badge>}
        {c?.trainingMode && <Badge variant="warning">Entrenando</Badge>}
        {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPanelOpen(true)}
          title="Detalles del contacto"
          aria-label="Detalles del contacto"
        >
          <Info className="h-4 w-4" />
        </Button>
        {(c?.unreadCount ?? 0) > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markRead.mutate()}
            loading={markRead.isPending}
            title="Marcar como leída"
          >
            <CheckCheck className="h-4 w-4" /> Leída
          </Button>
        )}
        {!isGroup && (entrena || c?.trainingMode) && (
          <Button
            variant={c?.trainingMode ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => training.mutate(!c?.trainingMode)}
            loading={training.isPending}
            title={
              c?.trainingMode
                ? 'Terminar el entrenamiento (el agente vuelve a responder)'
                : 'Modo entrenamiento: atiendes tú y tus respuestas se capturan como ejemplos'
            }
          >
            <GraduationCap className="h-4 w-4" />
            {c?.trainingMode ? 'Terminar' : 'Entrenar'}
          </Button>
        )}
        <Button
          variant={isHumanControlled ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => takeControl.mutate(!isHumanControlled)}
          loading={takeControl.isPending}
        >
          {isHumanControlled ? (
            <>
              <Bot className="h-4 w-4" /> Devolver a AI
            </>
          ) : (
            <>
              <UserCog className="h-4 w-4" /> Tomar control
            </>
          )}
        </Button>
        {hayTienda && !isGroup && supervisa && conversationId && (
          <PasarChat conversationId={conversationId} cliente={name} asignadaA={asignada} />
        )}
        {hayTienda && !isGroup && (
          <Button
            variant={esReferido ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={esReferido}
            onClick={() => referido.mutate(!esReferido)}
            loading={referido.isPending}
            title={esReferido ? 'Este cliente vino referido. Toca para quitar la marca' : 'Marca este chat si el cliente vino recomendado por alguien'}
          >
            <UserCheck className="h-4 w-4" /> {esReferido ? 'Referido' : 'Vino referido'}
          </Button>
        )}
        {hayTienda && !isGroup && conversationId && (
          seguimiento.data ? (
            <Link
              to="/seguimiento"
              title="Este cliente quedó en comprar: está en Seguimiento"
              className="inline-flex h-[34px] items-center gap-1.5 rounded-lg bg-amber-100 px-3.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            >
              <ListChecks className="h-4 w-4" /> Por cerrar
            </Link>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMarcandoSeguimiento(true)}
              title="Marca que este cliente quedó en comprar: sale en Seguimiento hasta que compre o diga que no"
            >
              <ListChecks className="h-4 w-4" /> Quedó en comprar
            </Button>
          )
        )}
        {hayTienda && !isGroup && (
          <Button size="sm" onClick={() => setPasandoVenta(true)} title="Registrar la venta de este cliente">
            <ShoppingBag className="h-4 w-4" /> Pasar venta
          </Button>
        )}
        {c?.status !== 'CLOSED' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmClose(true)}
            title="Cerrar la conversación (un mensaje nuevo del cliente la reabre)"
          >
            <XCircle className="h-4 w-4" /> Cerrar
          </Button>
        )}
      </div>
      {anuncio && (
        // De qué publicidad llegó: lo primero que tiene que ver quien atiende.
        <div className="flex min-w-0 items-start gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-xs dark:border-emerald-800/60 dark:bg-emerald-900/20">
          <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-300" />
          <div className="min-w-0 flex-1 break-words">
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">
              Llegó por un anuncio{anuncio.titulo ? ` · ${anuncio.titulo}` : ''}
            </p>
            {anuncio.cuerpo && (
              <p className="line-clamp-2 text-slate-600 dark:text-slate-300" title={anuncio.cuerpo}>
                {anuncio.cuerpo}
              </p>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Anuncio {anuncio.sourceId}
              {anuncio.sourceUrl && (
                <>
                  {' · '}
                  <a href={anuncio.sourceUrl} target="_blank" rel="noreferrer" className="underline">
                    Ver anuncio
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <Modal
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title="Cerrar conversación"
        description="La conversación sale de la bandeja activa y el asistente deja de responderla. Si el cliente vuelve a escribir, se reabre sola."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>
              Cancelar
            </Button>
            <Button loading={closeConversation.isPending} onClick={() => closeConversation.mutate()}>
              Cerrar conversación
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          ¿Cerrar la conversación con <span className="font-medium">{name}</span>?
        </p>
      </Modal>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-4">
        {messages.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : ordered.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">No hay mensajes todavía.</p>
        ) : (
          ordered.map((m) => (
            <MessageBubble key={m.id} message={m} showSender={c?.contact?.isGroup ?? false} />
          ))
        )}
      </div>

      {/* Composer */}
      {porQueNoSeEscribe ? (
        // NO SE FINGE UN ENVÍO. Con el simulacro puesto el mensaje quedaría
        // guardado como "mandado" sin haber salido, y Athena leería después que
        // el dueño contestó cuando no contestó. En un canal de relevo, contestar
        // desde acá se saltaría las bajas que guarda RESIST. El servidor también
        // lo rechaza; esto es para que no haga falta llegar al error.
        // Envuelve en renglones dentro de su caja y deja a la derecha el sitio del botón flotante (56 + 16).
        <div className="whitespace-normal break-words border-t border-surface-border bg-slate-50 py-3 pl-4 pr-20 text-center text-xs text-slate-500 [text-wrap:balance] dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
          {porQueNoSeEscribe}
        </div>
      ) : (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate(draft.trim());
        }}
        className="flex items-center gap-2 border-t border-surface-border bg-white px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-white/10 dark:bg-slate-950"
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe un mensaje…"
          className="h-11 min-w-0 flex-1 rounded-full border-0 bg-white px-4 text-base text-slate-900 caret-acento shadow-[0_0_0_1px_var(--mac-campo-filo)] placeholder:text-slate-400 focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 dark:bg-white/5 dark:text-slate-100 lateral:h-8 lateral:text-sm"
        />
        <Button type="submit" size="icon" className="rounded-full lateral:h-8 lateral:w-8" loading={send.isPending} disabled={!draft.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
      )}

      <Modal
        open={marcandoSeguimiento}
        onClose={() => setMarcandoSeguimiento(false)}
        title="Quedó en comprar"
        description="Sale en Seguimiento hasta que compre (se marca solo al confirmar su venta) o digas que no."
        footer={
          <>
            <Button variant="outline" onClick={() => setMarcandoSeguimiento(false)}>
              Cancelar
            </Button>
            <Button onClick={() => abrirSeguimiento.mutate()} loading={abrirSeguimiento.isPending}>
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <textarea
            value={notaSeguimiento}
            onChange={(e) => setNotaSeguimiento(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Qué quiere y qué quedó hablado (opcional)"
            aria-label="Nota"
            className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
          <label className="block space-y-1">
            <span className="text-xs text-slate-500">Volver a escribirle el (opcional)</span>
            <input
              type="date"
              value={volverEl}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setVolverEl(e.target.value)}
              className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
        </div>
      </Modal>

      {pasandoVenta && conversationId && (
        <PasarVenta
          conversationId={conversationId}
          nombreDelChat={c?.contact?.name ?? c?.contact?.pushName ?? ''}
          sucursalDelCanal={c?.channel?.ubicacionId ?? null}
          onCerrar={() => setPasandoVenta(false)}
        />
      )}

      <ContactDetailsPanel
        contactId={c?.contact?.id ?? c?.contactId}
        conversationId={c?.id ?? conversationId}
        conversationStatus={c?.status}
        leadStatus={c?.leadStatus ?? null}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  );
}
